import React, { useState, useRef, useEffect } from 'react';
import { Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import './zonaTransaccional.module.scss';

type MenuOption = {
  icon: string;
  label: string;
  href: string;
  highlighted?: boolean;
};

// Componente DropdownArrow
const DropdownArrow: React.FC<{isOpen: boolean}> = ({ isOpen }) => (
  <i
    className={`icon-chevron ${isOpen ? 'icon-chevron-up' : 'icon-chevron-down'}`}
    style={{
      fontSize: '.8rem',
      fontWeight: 'normal !important',
    }}
    aria-hidden="true"
  />
);

const ZonaTransaccional: React.FC = () => {
  const theme = useTheme();
  const [isZonaOpen, setIsZonaOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0 });

  const menuOptions: MenuOption[] = [
    {
      icon: "icon-users",
      label: "Personas",
      href: "https://www.comfama.com/portal-servicios/personas/"
    },
    {
      icon: "icon-industry",
      label: "Empresas",
      href: "https://www.comfama.com/portal-servicios/empresas/",
    }
  ];
  
  // Calcular posición exacta del dropdown cuando se abre
  useEffect(() => {
    if (isZonaOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const buttonCenterX = buttonRect.left + buttonRect.width / 2;
      const dropdownWidth = 250; // Ancho del dropdown en píxeles
      const leftPosition = buttonCenterX - dropdownWidth / 2;
      
      setDropdownPosition({ left: leftPosition });
    }
  }, [isZonaOpen]);

  
  useEffect(() => {
    const handleResize = () => {
      if (isZonaOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const buttonCenterX = buttonRect.left + buttonRect.width / 2;
        const dropdownWidth = 250; 
        const leftPosition = buttonCenterX - dropdownWidth / 2;
        
        setDropdownPosition({ left: leftPosition });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isZonaOpen]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isZonaOpen && 
        buttonRef.current && 
        dropdownRef.current && 
        !buttonRef.current.contains(event.target as Node) && 
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsZonaOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isZonaOpen]);


  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (isZonaOpen && event.key === 'Escape') {
        setIsZonaOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isZonaOpen]);

  // Estilos originales
  const buttonSx = {
    backgroundColor: '#EE2B7B',
    color: '#fff',
    width: '151px',
    height: '33px',
    padding: '8px 8px',
    fontWeight: 'normal',
    borderRadius: '20px',
    fontSize: '.9rem',
    [theme.breakpoints.up('md')]: {
      width: '186px',
      height: '37px',
      borderRadius: '10px',
    },
    '&:hover': {
      backgroundColor: '#EE2B7B',
    },
    '&:focus-visible': {
      outline: '2px solid #000',
      outlineOffset: '2px',
    },
  };

  const handleToggleMenu = () => {
    setIsZonaOpen(!isZonaOpen);
  };

  return (
    <div className="dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        ref={buttonRef}
        role="button"
        id="zona-transaccional-button"
        aria-controls={isZonaOpen ? "zona-transaccional-menu" : undefined}
        aria-label="Menú zona transaccional"
        onClick={handleToggleMenu}
        className="ml-2"
        variant="contained"
        endIcon={!isMobile ? <DropdownArrow isOpen={isZonaOpen} /> : null}
        sx={buttonSx}
      >
        Zona transaccional
      </Button>
      
      {isZonaOpen && (
        <div 
          ref={dropdownRef}
          className="dropdown-content"
          style={{ 
            position: 'fixed', 
            top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: dropdownPosition.left,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            width: '250px',
            padding: '12px',
            zIndex: 1500,
          }}
        >
          <div className="dropdown-menu" style={{ width: '100%' }}>
            {menuOptions.map((option, index) => (
              <a 
                key={index}
                href={option.href}
                className="dropdown-item"
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                aria-label={`zona transaccional ${option.label}`}
                onClick={() => setIsZonaOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333',
                  textDecoration: 'none',
                  borderRadius: '4px',
                }}
              >
                <i 
                  className={`${option.icon} dropdown-menu-icon`} 
                  aria-hidden="true" 
                  style={{ 
                    marginRight: '10px', 
                    color: option.highlighted ? '#EE2B7B' : '#ff4081'
                  }} 
                />
                {option.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZonaTransaccional;